/**
 * @file 灰度发布状态 Store
 * @description 缓存当前登录组员的灰度命中结果，并提供统一判断入口。
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { getCurrentGrayFeatures } from '@/api/grayRelease'
import { loginUserStore } from '@/stores/modules/loginUser'

/** 灰度功能命中表。 */
type GrayDecisionMap = Record<string, boolean>

/**
 * 灰度发布 Store。
 * @returns 当前灰度命中表、加载状态和判断方法。
 */
export const grayReleaseStore = defineStore('tdm-grayReleaseStore', () => {
  /** 当前组员灰度命中表。 */
  const decisions = ref<GrayDecisionMap>({})

  /** 当前是否正在拉取灰度命中结果。 */
  const loading = ref(false)

  /** 当前是否已完成一次加载。 */
  const loaded = ref(false)

  /** 当前刷新请求 Promise，用于合并并发刷新。 */
  let fetchingPromise: Promise<void> | null = null

  /** 身份变化时换代，旧身份的响应不能回填当前灰度状态。 */
  let epoch = 0

  /** 是否已绑定登录态监听。 */
  let loginWatcherBound = false

  /** 清空当前灰度命中表。 */
  const clear = (): void => {
    epoch++
    fetchingPromise = null
    loading.value = false
    decisions.value = {}
    loaded.value = true
  }

  /** 刷新当前组员灰度命中结果。 */
  const refreshCurrent = async (): Promise<void> => {
    if (fetchingPromise) {
      return fetchingPromise
    }
    const generation = epoch
    loading.value = true
    // 后台刷新保留当前命中结果，避免每次刷新都卸载评论区、丢失草稿。
    fetchingPromise = (async () => {
      try {
        const res = await getCurrentGrayFeatures()
        if (generation !== epoch) return
        decisions.value = res.data.data?.features ?? {}
        loaded.value = true
      } catch {
        if (generation === epoch) {
          decisions.value = {}
          loaded.value = true
        }
      } finally {
        if (generation === epoch) {
          loading.value = false
          fetchingPromise = null
        }
      }
    })()
    return fetchingPromise
  }

  /**
   * 判断功能是否命中灰度。
   * @param featureKey 功能唯一标识。
   * @returns 已加载且后端判定命中时返回 true。
   */
  const isEnabled = (featureKey: string): boolean => {
    return loaded.value && decisions.value[featureKey] === true
  }

  /** 绑定身份变化；游客也查询公开功能命中结果，退出时立即隔离旧账号响应。 */
  const bindLoginState = (): void => {
    if (loginWatcherBound) return
    loginWatcherBound = true
    const memberStore = loginUserStore()
    watch(
      () => memberStore.token,
      () => {
        clear()
        loaded.value = false
        void refreshCurrent()
      },
      { immediate: true, flush: 'sync' }
    )
  }

  return {
    decisions,
    loading,
    loaded,
    clear,
    refreshCurrent,
    isEnabled,
    bindLoginState
  }
})
